import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { entries, flow, groupBy, map, partition } from 'lodash/fp';
import { ArrowForward as ArrowForwardIcon, Info as InfoIcon } from '@material-ui/icons';
import { Button, Divider, Grid, makeStyles, Paper, Typography, Box } from '@material-ui/core';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import StatItem, { Props as StatItemProps } from 'components/@common/StatItem';
import useProductCache, { PliManagedApp } from 'store/cache/product-cache';
import { OrganizationLicenseUsage, Product, ProductType } from 'types/schema-generated-types';
import { aggregateUsages } from 'utilities/product-utils';
import LicenseChart from './components/LicenseChart';
import ProductsTable from './components/ProductsTable';

export type Props = {
  licenseUsage: OrganizationLicenseUsage[];
  rootOrganizationLicenseUsage?: OrganizationLicenseUsage[];
  onLicenseDetailsClick?: (productCode: string) => void;
};

interface ProductUsageProps {
  licenseUsage: OrganizationLicenseUsage[];
  rootOrganizationLicenseUsage?: OrganizationLicenseUsage[];
  product: Product;
  onLicenseDetailsClick?: (productCode: string) => void;
}

interface OverageWarningProps {
  app?: PliManagedApp | null;
  count?: number;
}

type ProductLicenseUsage = [
  Product,
  OrganizationLicenseUsage[],
  OrganizationLicenseUsage[] | undefined
];

const OverageWarning = ({ app, count = 0 }: OverageWarningProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ProductAndLicenses');

  const webstoreUrl = app?.purchaseDetails?.webstoreUrl;
  const quoteUrl = app?.purchaseDetails?.salesQuoteUrl;
  const hasActions = !!webstoreUrl || !!quoteUrl;

  return (
    <Grid container justify="center" className={classes.overageWarningRoot}>
      <Grid item>
        <Typography>
          {count > 0 ? (
            /* prettier-ignore */
            <Trans t={t} i18nKey="usingLicenseOverage" count={count}>
              Your license usage is above your purchased licenses by <strong>{{ count }} license</strong>
            </Trans>
          ) : (
            t('noLicensesAvailable')
          )}
        </Typography>
        <Typography>{t('callToAction')}</Typography>
      </Grid>
      {hasActions && (
        <Grid item container spacing={1} justify="center" style={{ marginTop: 16 }}>
          {webstoreUrl && (
            <Grid item>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<ShoppingCartIcon />}
                href={webstoreUrl}
                target="_blank"
              >
                {t('orderOnline')}
              </Button>
            </Grid>
          )}
          {quoteUrl && (
            <Grid item>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<InfoIcon />}
                href={quoteUrl}
                target="_blank"
              >
                {t('requestQuote')}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const LicenseStatItem = ({
  title,
  count,
  severity
}: Pick<StatItemProps, 'title' | 'count' | 'severity'>) => {
  const classes = useStyles();
  return (
    <StatItem
      size="small"
      title={title}
      count={count}
      className={classes.statItem}
      severity={severity}
    />
  );
};

const ProductUsage = ({
  product,
  licenseUsage,
  rootOrganizationLicenseUsage,
  onLicenseDetailsClick
}: ProductUsageProps) => {
  const classes = useStyles();
  const { t } = useTranslation('AdminProductLicenses');
  const [{ findAppByProductCode }] = useProductCache();

  let {
    siteLicensed,
    assignedCount,
    realAvailableCount,
    realTotalCount,
    expiringCount
  } = aggregateUsages(licenseUsage);

  // If this is a suborganization (i.e. rootOrganizationLicenseUsage exists here), we need to calculate things a little differently...
  if (rootOrganizationLicenseUsage) {
    // Overage and available licenses are calculated at the root level, get the root's 'real' available license count.
    const rootRealAvailableCount = aggregateUsages(rootOrganizationLicenseUsage).realAvailableCount;
    if (rootRealAvailableCount > 0) {
      realAvailableCount = rootRealAvailableCount; // If there are *any* available licenses at the root, then they'll be available to any suborganization.
      realTotalCount = assignedCount + realAvailableCount; // Total count here will be the suborganization's assigned count plus root's available count.
    }
  }

  const overageCount = assignedCount > realTotalCount ? assignedCount - realTotalCount : 0;
  const availableCount = overageCount > 0 ? 0 : realAvailableCount;

  const showOverageWarning = !siteLicensed && (availableCount === 0 || overageCount > 0);

  const app = findAppByProductCode(product.code);

  return (
    <Grid item container spacing={1} className={classes.productRoot}>
      <Grid item xs={12} className={classes.productHeader}>
        <img src={product.iconUrl ?? ''} alt={product.name} height={32} width={32} />
        <Typography variant="h3">{product.name}</Typography>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md="auto">
          <Box className={classes.statContainer}>
            <Box className={classes.statItemContainer}>
              <LicenseStatItem title={t('statsTotal')} count={realTotalCount} />
              <LicenseStatItem
                title={t('statsAssigned')}
                count={assignedCount}
                {...(overageCount > 0 && { severity: 'error' })}
              />
              <LicenseStatItem title={t('statsAvailable')} count={availableCount} />
              <LicenseStatItem title={t('statsExpiringSoon')} count={expiringCount} />
            </Box>
            {onLicenseDetailsClick && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={() => onLicenseDetailsClick(product.code)}
                startIcon={<ArrowForwardIcon />}
              >
                {t('licenseDetailsButton')}
              </Button>
            )}
          </Box>
        </Grid>
        {showOverageWarning && (
          <Grid item sm={12} md className={classes.overageWarningItem}>
            <OverageWarning app={app} count={overageCount} />
          </Grid>
        )}
        <Grid container item spacing={1}>
          <Grid item xs={12} md="auto" className={classes.chartContainer}>
            <LicenseChart
              totalCount={realTotalCount}
              assignedCount={assignedCount}
              expiringSoonCount={expiringCount}
              overageCount={overageCount}
            />
          </Grid>
          <Grid item xs={12} md>
            <ProductsTable licenseUsage={licenseUsage} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ProductFamilyUsageCard = ({
  licenseUsage,
  rootOrganizationLicenseUsage,
  onLicenseDetailsClick
}: Props) => {
  const classes = useStyles();

  const [studentProductLicenseUsage, userProductLicenseUsage]: [
    ProductLicenseUsage[],
    ProductLicenseUsage[]
  ] = flow(
    groupBy('product.id'),
    entries,
    map(([productId, usages]: [number, OrganizationLicenseUsage[]]) => {
      const rootUsages = rootOrganizationLicenseUsage?.filter(u => u.productId === +productId);
      return [usages[0].product, usages, rootUsages];
    }),
    partition(([product]: [Product]) => product.type === ProductType.STUDENT)
  )(licenseUsage);

  const showStudentUsage = studentProductLicenseUsage.length > 0;
  const showUserUsage = userProductLicenseUsage.length > 0;

  return (
    <Grid component={Paper} direction="column" container className={classes.root}>
      {showStudentUsage &&
        studentProductLicenseUsage.map(([product, licenseUsage, rootUsages]) => (
          <ProductUsage
            key={`student-product-usage-${product.code}`}
            rootOrganizationLicenseUsage={rootUsages}
            licenseUsage={licenseUsage}
            product={product}
            onLicenseDetailsClick={onLicenseDetailsClick}
          />
        ))}
      {showStudentUsage && showUserUsage && <Divider className={classes.divider} />}
      {showUserUsage &&
        userProductLicenseUsage.map(([product, licenseUsage, rootUsages]) => (
          <ProductUsage
            key={`user-product-usage-${product.code}`}
            rootOrganizationLicenseUsage={rootUsages}
            licenseUsage={licenseUsage}
            product={product}
            onLicenseDetailsClick={onLicenseDetailsClick}
          />
        ))}
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1)
  },
  productHeader: {
    display: 'flex',
    gap: `${theme.spacing(1)}px`
  },
  productRoot: {
    padding: theme.spacing(1)
  },
  overageWarningRoot: {
    textAlign: 'center',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    [theme.breakpoints.up(900)]: {
      minWidth: 400
    }
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  chartContainer: {
    alignSelf: 'center'
  },
  overageWarningItem: {
    display: 'flex',
    alignSelf: 'start'
  },
  statContainer: {
    display: 'grid',
    width: '100%',
    gap: `${theme.spacing(1)}px`,
    '& > button': {
      justifySelf: 'start'
    }
  },
  statItemContainer: {
    display: 'flex',
    gap: `${theme.spacing(1)}px`,
    [theme.breakpoints.down(900)]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      width: '100%'
    }
  },
  statItem: {
    margin: 0,
    [theme.breakpoints.up(900)]: {
      minWidth: 100
    }
  }
}));

export default ProductFamilyUsageCard;
